import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import InputField from 'components/InputField';
import { Field, useFormState } from 'react-final-form';
import SelectField from 'components/SelectField';
import { KeyEvent } from 'types';
import { isEnterPressed } from 'utils';
import { Button } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { SCORE } from 'models/DealRoom';
import PencilIcon from 'icons/Pencil.icon';
import get from 'lodash.get';
import validations from 'validations';
import DraggableCard from 'components/DraggableCard';
import DragIcon from 'icons/Drag.icon';
import usePress from 'hooks/usePress';
import useMatchMedia from 'hooks/useMatchMedia';

import './Answer.styles.scss';
import './Answer.styles.responsive.scss';

type AnswerProps = {
  className?: string;
  id: string;
  name: string;
  isReordering: boolean;
  onRemove: () => void;
  setIsReordering: React.Dispatch<React.SetStateAction<boolean>>;
};

const Answer: React.FC<AnswerProps> = (props) => {
  const { className, id, name, isReordering, onRemove, setIsReordering } =
    props;

  const { values } = useFormState();

  const value = get(values, name) || {};

  const { answer, autoFocus } = value;

  const [isEdit, setIsEdit] = useState(autoFocus);

  const isPhablet = useMatchMedia('isPhablet');

  const createOptions = useCreateSelectOptions();

  const scoreOptions = createOptions(Object.values(SCORE));

  const classes = classNames('answer', className);

  const events = usePress({
    onPressStart: () => setIsReordering(true),
    onPressEnd: () => setIsReordering(false),
  });

  const Content = useMemo(() => {
    return isEdit ? (
      <div>
        <Field
          name={`${name}.answer`}
          component={InputField}
          autoFocus
          validate={validations.required()}
          customOnBlur={() => setIsEdit(false)}
          onKeyPress={(e: KeyEvent) => {
            if (isEnterPressed(e)) {
              setIsEdit(false);
            }
          }}
        />
      </div>
    ) : (
      <>
        <div>
          <Field
            name={`${name}.answer`}
            component="input"
            type="hidden"
            validate={validations.required()}
          />
          <p>{answer}</p>
          <Button
            variant="icon"
            className="edit-button"
            icon={PencilIcon}
            onClick={() => setIsEdit(true)}
          />
        </div>

        <div>
          <Field
            name={`${name}.score`}
            component={SelectField}
            options={scoreOptions}
            renderAsPortal
            portalClassName="priority-dropdown-options"
          />
          <Button
            variant="icon"
            icon={TrashIcon}
            className="remove-button"
            onClick={onRemove}
          />
          {!isPhablet && <Button variant="icon" icon={DragIcon} {...events} />}
        </div>
      </>
    );
  }, [isEdit, name, answer, scoreOptions, events, isPhablet, onRemove]);

  return (
    <DraggableCard className={classes} id={id} dragEnabled={isReordering}>
      {Content}
    </DraggableCard>
  );
};

export default Answer;
